
import { WaterLog, ActivityLog, ScaleLog } from '../types';

/**
 * K_BASE: Basal water loss rate.
 * A healthy adult loses ~2500ml/day. 
 */
const K_BASE = 0.0025; 
const STEP_MINUTES = 15; 

export interface HydrationPoint {
    hour: number;
    level: number; // 0.0 to 1.0 (0% to 100%)
}

/**
 * Calculates Total Body Water (TBW) in Liters using the Watson Formula.
 */
export function calculateWatsonTBW(
    gender: 'male' | 'female',
    weightKg: number,
    heightCm: number,
    ageYears: number
): number {
    if (gender === 'male') {
        return 2.447 - (0.09156 * ageYears) + (0.1074 * heightCm) + (0.3362 * weightKg);
    } else {
        return -2.097 + (0.1069 * heightCm) + (0.2466 * weightKg);
    }
}

/**
 * Simulates biological hydration levels over a 24-hour period.
 * Manual measurements (measuredPercentage) are treated as "Truth Points".
 * The simulation follows the biological decay until a truth point is hit, 
 * then restarts math from that point.
 */
export function simulateDayHydration(
    weightKg: number,
    waterLogs: WaterLog[],
    activityLogs: ActivityLog[],
    targetDate: Date = new Date(),
    profile?: { gender: 'male' | 'female', height: number, age: number },
    scaleLogs: ScaleLog[] = []
): HydrationPoint[] {
    const volumeCorps = profile 
        ? calculateWatsonTBW(profile.gender, weightKg, profile.height, profile.age)
        : 0.6 * weightKg; 

    const result: HydrationPoint[] = [];
    
    // Default initial state at Midnight (aiming for 55% at 7am if no measures exist)
    let H = 0.5597; 
    const dateStr = targetDate.toDateString();

    const dayWater = waterLogs.filter(l => new Date(l.timestamp).toDateString() === dateStr);
    const dayActivities = activityLogs.filter(l => new Date(l.timestamp).toDateString() === dateStr);
    const dayScales = scaleLogs.filter(l => new Date(l.timestamp).toDateString() === dateStr && l.hydrationPercentage !== undefined);

    for (let m = 0; m <= 1440; m += STEP_MINUTES) {
        const currentHour = m / 60;
        const dt = STEP_MINUTES / 60;

        // 1. Check for manual measurements (Truth Points) in this interval
        // We prioritize ScaleLogs for hydration measure if available, then fallback to legacy WaterLog measures
        const scaleMeasure = dayScales.find(log => {
            const d = new Date(log.timestamp);
            const logM = d.getHours() * 60 + d.getMinutes();
            return logM >= m && logM < m + STEP_MINUTES;
        });

        const waterMeasure = dayWater.find(log => {
            if (log.measuredPercentage === undefined) return false;
            const d = new Date(log.timestamp);
            const logM = d.getHours() * 60 + d.getMinutes();
            return logM >= m && logM < m + STEP_MINUTES;
        });

        if (scaleMeasure && scaleMeasure.hydrationPercentage !== undefined) {
            H = scaleMeasure.hydrationPercentage / 100;
        } else if (waterMeasure && waterMeasure.measuredPercentage !== undefined) {
            H = waterMeasure.measuredPercentage / 100;
        }

        // 2. Calculate Metabolic Decay
        let k = K_BASE;
        for (const act of dayActivities) {
            const [h, min] = act.startTime.split(':').map(Number);
            const actStartM = h * 60 + min;
            const actEndM = actStartM + act.duration;
            
            if (m >= actStartM && m <= actEndM) {
                const tempEffect = 0.0005 * ((act.temperature || 22) - 22);
                const intensityEffect = 0.00002 * (act.calories / Math.max(1, act.duration)); 
                k += tempEffect + intensityEffect;
            }
        }

        H = H * Math.exp(-k * dt);

        // 3. Add Hydration Gains (ml intake)
        for (const log of dayWater) {
            if (log.amount === undefined) continue; // Skip if it was just a measurement
            const d = new Date(log.timestamp);
            const logM = d.getHours() * 60 + d.getMinutes();
            if (logM >= m && logM < m + STEP_MINUTES) {
                const gain = (log.amount / 1000) / volumeCorps;
                H = Math.min(1.0, H + gain); 
            }
        }

        result.push({ hour: currentHour, level: H });
    }

    return result;
}